import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

// Custom scheme (not tied to the bundle ID, which can differ between
// personal-signing and distribution builds) — must also be added to
// Supabase Auth > URL Configuration > Redirect URLs.
export const NATIVE_AUTH_REDIRECT = "chillikoala://auth-callback";

export const isNativePlatform = () => Capacitor.isNativePlatform();

// Sign in with Apple is only required by App Store guideline 4.8 for the
// native iOS app — Google Play has no equivalent requirement, so the button
// stays iOS-only rather than also appearing on Android/web.
export const isIOSPlatform = () => Capacitor.getPlatform() === "ios";

// Google (and any OAuth provider) sign-in inside a Capacitor app: opening the
// provider's page via a normal top-level redirect either bounces out to
// Safari/Chrome and strands the user there, or gets blocked by Google as an
// embedded webview. Instead we fetch the OAuth URL without redirecting
// (skipBrowserRedirect), show it in an in-app browser tab, and complete the
// session when the OAuth redirect lands back on our custom URL scheme.
export const signInWithGoogleNative = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: NATIVE_AUTH_REDIRECT,
      skipBrowserRedirect: true,
    },
  });
  if (error) throw error;
  if (data?.url) {
    await Browser.open({ url: data.url, presentationStyle: "fullscreen" });
  }
};

// Apple sign-in reuses the same in-app-browser + custom-scheme redirect
// pattern as Google — Supabase's own callback URL satisfies Apple's
// HTTPS-only redirect_uri requirement, and Supabase does the final
// client-side redirect to our custom scheme, same as any other provider.
//
// Always fullscreen, never "popover": on iPhone the plugin silently downgrades
// popover to fullscreen (compact size class), but on iPad it really does present
// SFSafariViewController in a small centred popover, which is where Apple's
// reviewer hit "an error occurred" on an iPad Air. Fullscreen behaves
// identically on both device families.
export const signInWithAppleNative = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "apple",
    options: {
      redirectTo: NATIVE_AUTH_REDIRECT,
      skipBrowserRedirect: true,
    },
  });
  if (error) throw error;
  if (data?.url) {
    await Browser.open({ url: data.url, presentationStyle: "fullscreen" });
  }
};

// Registered once at app startup — completes the session when the in-app
// browser redirects back to our custom scheme, then closes the browser tab.
export const setupNativeAuthListener = () => {
  if (!isNativePlatform()) return () => {};

  const listenerPromise = CapacitorApp.addListener("appUrlOpen", async ({ url }) => {
    if (!url.startsWith(NATIVE_AUTH_REDIRECT)) return;
    try {
      // exchangeCodeForSession wants the bare auth code, not the full redirect
      // URL — passing the URL itself silently fails the exchange.
      const params = new URL(url).searchParams;
      const errorDescription = params.get("error_description") || params.get("error");
      if (errorDescription) throw new Error(errorDescription);

      const code = params.get("code");
      if (!code) throw new Error("No authorization code in redirect URL");

      const { error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) throw error;
    } catch (err) {
      console.error("Failed to complete native OAuth sign-in:", err);
      toast.error("Sign-in failed. Please try again.");
    } finally {
      Browser.close().catch(() => {});
    }
  });

  return () => {
    listenerPromise.then((listener) => listener.remove());
  };
};
